import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Chip,
  Avatar,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ShoppingCart as ShoppingCartIcon,
  Receipt as ReceiptIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  LocalOffer as VoucherIcon,
  CardMembership as AffiliateIcon,
  Star as StarIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { usersService } from '../services/usersService';
import { useNotification } from '../contexts/NotificationContext';
import UserVouchersModal from '../components/UserVouchersModal';
import affiliateService from '../services/affiliateService';
import referralService from '../services/referralService';

// Composant pour afficher les détails d'un utilisateur
const UserDetailsModal = ({ open, onClose, user, onUserUpdate }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [affiliateData, setAffiliateData] = useState(null);
  const [loadingAffiliate, setLoadingAffiliate] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [loadingReferral, setLoadingReferral] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    if (open && user) {
      loadUserDetails();
      loadAffiliateData();
      loadReferralData();
    }
  }, [open, user]);

  const loadUserDetails = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const response = await usersService.getUserById(user.id);
      setUserData(response.user);
    } catch (error) {
      showNotification('Erreur lors du chargement des détails', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAffiliateData = async () => {
    if (!user) return;
    
    setLoadingAffiliate(true);
    try {
      // Récupérer les affiliés et chercher celui qui correspond à cet utilisateur
      const affiliatesResponse = await affiliateService.getAffiliates();
      const affiliates = affiliatesResponse.data || [];
      const userAffiliate = affiliates.find(aff => aff.userId === user.id);
      setAffiliateData(userAffiliate || null);
    } catch (error) {
      // Si l'utilisateur n'est pas affilié, c'est normal
      setAffiliateData(null);
    } finally {
      setLoadingAffiliate(false);
    }
  };

  const loadReferralData = async () => {
    if (!user) return;
    
    setLoadingReferral(true);
    try {
      const response = await referralService.getUserReferralStats(user.id);
      setReferralData(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données de parrainage:', error);
      setReferralData(null);
    } finally {
      setLoadingReferral(false);
    }
  };

  const handleCopyReferralCode = () => {
    if (referralData?.referralCode) {
      navigator.clipboard.writeText(referralData.referralCode);
      showNotification('Code de parrainage copié !', 'success');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPrice = (price) => {
    const n = Number(price);
    const safe = Number.isFinite(n) ? n : 0;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(safe);
  };

  if (!userData && !isLoading) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
        mb: 0,
        fontWeight: 600,
        fontSize: '1.25rem'
      }}>
        Détails de l'utilisateur : {(user?.first_name || 'Non renseigné')} {(user?.last_name || 'Non renseigné')}
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : userData ? (
          <Box sx={{ p: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
              <Tab icon={<PersonIcon />} label="Informations générales" />
              <Tab icon={<ShoppingCartIcon />} label={`Panier (${userData.stats?.cartItems || 0})`} />
              <Tab icon={<ReceiptIcon />} label={`Commandes (${userData.stats?.orders || 0})`} />
              <Tab icon={<LocationOnIcon />} label={`Adresses (${userData.stats?.addresses || 0})`} />
              <Tab icon={<AffiliateIcon />} label={affiliateData ? "Programme d'affiliation" : "Non affilié"} />
              <Tab icon={<ShareIcon />} label={`Parrainage (${referralData?.totalReferrals || 0})`} />
            </Tabs>

            {/* Onglet Informations générales */}
            {activeTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                        Informations personnelles
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Nom complet:
                          </Typography>
                          <Typography variant="body2">
                            {(userData.first_name || 'Non renseigné')} {(userData.last_name || 'Non renseigné')}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Email:
                          </Typography>
                          <Typography variant="body2">
                            {userData.email}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Date de naissance:
                          </Typography>
                          <Typography variant="body2">
                            {userData.date_of_birth ? formatDate(userData.date_of_birth) : 'Non renseignée'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Membre depuis:
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(userData.created_at)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                        Profil de fidélité
                      </Typography>
                      {userData.profil ? (
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Niveau:
                            </Typography>
                            <Typography variant="body2">
                              {userData.profil.level || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              XP:
                            </Typography>
                            <Typography variant="body2">
                              {userData.profil.xp || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Points:
                            </Typography>
                            <Typography variant="body2">
                              {userData.profil.points || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              XP vers niveau suivant:
                            </Typography>
                            <Typography variant="body2">
                              {userData.profil.xp_to_next_level || 0}
                            </Typography>
                          </Grid>
                        </Grid>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Aucun profil de fidélité
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined" sx={{
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                        Statistiques
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="text.secondary">
                            Articles dans le panier:
                          </Typography>
                          <Typography variant="h6" color="primary.main">
                            {userData.stats?.cartItems || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="text.secondary">
                            Commandes:
                          </Typography>
                          <Typography variant="h6" color="primary.main">
                            {userData.stats?.orders || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="text.secondary">
                            Total dépensé:
                          </Typography>
                          <Typography variant="h6" color="primary.main">
                            {formatPrice(userData.stats?.totalSpent || 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="text.secondary">
                            Adresses:
                          </Typography>
                          <Typography variant="h6" color="primary.main">
                            {userData.stats?.addresses || 0}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Onglet Panier */}
            {activeTab === 1 && (
              <Box>
                {userData.cart?.items?.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Produit</TableCell>
                          <TableCell>Variante</TableCell>
                          <TableCell align="right">Quantité</TableCell>
                          <TableCell align="right">Prix unitaire</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userData.cart.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.productName}</TableCell>
                            <TableCell>{item.variantName}</TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">{formatPrice(item.priceTtc)}</TableCell>
                            <TableCell align="right">{formatPrice(item.priceTtc * item.quantity)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    Aucun article dans le panier
                  </Alert>
                )}
              </Box>
            )}

            {/* Onglet Commandes */}
            {activeTab === 2 && (
              <Box>
                {userData.orders?.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Numéro</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell align="right">Montant</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Articles</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userData.orders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>{order.orderNumber}</TableCell>
                            <TableCell>
                              <Chip 
                                label={order.status} 
                                color={order.status === 'confirmed' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">{formatPrice(order.totalAmount)}</TableCell>
                            <TableCell>{formatDate(order.createdAt)}</TableCell>
                            <TableCell align="right">{order.itemsCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    Aucune commande
                  </Alert>
                )}
              </Box>
            )}

            {/* Onglet Adresses */}
            {activeTab === 3 && (
              <Box>
                {userData.addresses?.length > 0 ? (
                  <Grid container spacing={2}>
                    {userData.addresses.map((address) => (
                      <Grid item xs={12} md={6} key={address.id}>
                        <Card variant="outlined" sx={{
                          backgroundColor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              {address.label}
                              {(address.isBilling || address.isShipping) && (
                                <Chip 
                                  label={address.isBilling ? 'Facturation' : 'Livraison'} 
                                  size="small" 
                                  sx={{ ml: 1 }} 
                                />
                              )}
                            </Typography>
                            <Typography variant="body2">
                              {address.fullName}
                            </Typography>
                            <Typography variant="body2">
                              {address.line1}
                            </Typography>
                            {address.line2 && (
                              <Typography variant="body2">
                                {address.line2}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              {address.postalCode} {address.city}
                            </Typography>
                            <Typography variant="body2">
                              {address.country}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Alert severity="info">
                    Aucune adresse enregistrée
                  </Alert>
                )}
              </Box>
            )}

            {/* Onglet Affilié */}
            {activeTab === 4 && (
              <Box>
                {loadingAffiliate ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </Box>
                ) : affiliateData ? (
                  <Grid container spacing={3}>
                    {/* Informations d'affiliation */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{
                        backgroundColor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                            Informations d'affiliation
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" color="text.secondary">
                                Code affilié:
                              </Typography>
                              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                                {affiliateData.affiliateCode}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" color="text.secondary">
                                Statut:
                              </Typography>
                              <Chip 
                                label={affiliateData.isActive ? 'Actif' : 'Inactif'} 
                                color={affiliateData.isActive ? 'success' : 'default'}
                                sx={{ mt: 0.5 }}
                              />
                            </Grid>
                            {affiliateData.companyName && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Entreprise:
                                </Typography>
                                <Typography variant="body2">
                                  {affiliateData.companyName}
                                </Typography>
                              </Grid>
                            )}
                            {affiliateData.website && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Site web:
                                </Typography>
                                <Typography variant="body2">
                                  {affiliateData.website}
                                </Typography>
                              </Grid>
                            )}
                            {affiliateData.socialMedia && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Réseaux sociaux:
                                </Typography>
                                <Typography variant="body2">
                                  {affiliateData.socialMedia}
                                </Typography>
                              </Grid>
                            )}
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                Membre depuis:
                              </Typography>
                              <Typography variant="body2">
                                {formatDate(affiliateData.createdAt)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Statistiques */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                        Statistiques
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ bgcolor: 'success.dark' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            Avis publiés
                          </Typography>
                          <Typography variant="h3">{affiliateData.totalReviews || 0}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ bgcolor: 'primary.dark' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            Demandes de produits
                          </Typography>
                          <Typography variant="h3">{affiliateData.totalProductRequests || 0}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ bgcolor: 'warning.dark' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            Note moyenne
                          </Typography>
                          <Box display="flex" alignItems="center">
                            <Typography variant="h3">
                              {affiliateData.averageRating && typeof affiliateData.averageRating === 'number'
                                ? affiliateData.averageRating.toFixed(1)
                                : '-'}
                            </Typography>
                            {affiliateData.averageRating && typeof affiliateData.averageRating === 'number' && (
                              <StarIcon sx={{ fontSize: 40, ml: 1, color: 'warning.light' }} />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Cet utilisateur ne fait pas partie du programme d'affiliation
                  </Alert>
                )}
              </Box>
            )}

            {/* Onglet Parrainage */}
            {activeTab === 5 && (
              <Box>
                {loadingReferral ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={3}>
                    {/* Code de parrainage */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{
                        backgroundColor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                            Votre code de parrainage
                          </Typography>
                          <Box display="flex" alignItems="center" gap={2} mt={2}>
                            <Typography variant="h4" sx={{ 
                              color: 'primary.main',
                              fontFamily: 'monospace',
                              letterSpacing: 2,
                              fontWeight: 'bold'
                            }}>
                              {referralData?.referralCode || 'N/A'}
                            </Typography>
                            <IconButton 
                              onClick={handleCopyReferralCode}
                              color="primary"
                              title="Copier le code"
                            >
                              <CopyIcon />
                            </IconButton>
                          </Box>
                          <Typography variant="body2" color="text.secondary" mt={2}>
                            Partagez ce code avec vos amis pour qu'ils bénéficient d'avantages lors de leur inscription
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Si l'utilisateur a un parrain */}
                    {referralData?.referrer && (
                      <Grid item xs={12}>
                        <Alert severity="info">
                          Parrainé par : {referralData.referrer.firstName} {referralData.referrer.lastName} ({referralData.referrer.referralCode})
                        </Alert>
                      </Grid>
                    )}

                    {/* Info validation */}
                    <Grid item xs={12}>
                      <Alert severity="info" variant="outlined">
                        Un filleul est validé après avoir dépensé 50€ en commandes payées. 
                        Seuls les filleuls validés rapportent des récompenses !
                      </Alert>
                    </Grid>

                    {/* Statistiques */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                        Statistiques
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Card sx={{ bgcolor: 'success.dark' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            Total filleuls
                          </Typography>
                          <Typography variant="h3">{referralData?.totalReferrals || 0}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Card sx={{ bgcolor: 'info.dark' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            Filleuls validés
                          </Typography>
                          <Typography variant="h3">{referralData?.validatedReferrals || 0}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Card sx={{ bgcolor: 'primary.dark' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            Points gagnés
                          </Typography>
                          <Typography variant="h3">
                            {referralData?.referralPoints || 0} pts
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Card sx={{ bgcolor: 'warning.dark' }}>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom variant="body2">
                            XP gagné
                          </Typography>
                          <Typography variant="h3">
                            {referralData?.referralXp || 0} XP
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Liste des filleuls */}
                    {referralData?.referrals && referralData.referrals.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                          Mes filleuls ({referralData.referrals.length})
                        </Typography>
                        <Paper variant="outlined">
                          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                            <Grid container spacing={2} sx={{ p: 2 }}>
                              {referralData.referrals.map((referral) => (
                                <Grid item xs={12} md={6} key={referral.id}>
                                  <Card variant="outlined" sx={{
                                    borderColor: referral.isValidated ? 'success.main' : 'divider',
                                    borderWidth: referral.isValidated ? 2 : 1,
                                  }}>
                                    <CardContent>
                                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                        <Box>
                                          <Typography variant="subtitle1" fontWeight="bold">
                                            {referral.firstName} {referral.lastName}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            {referral.email}
                                          </Typography>
                                        </Box>
                                        {referral.isValidated ? (
                                          <Chip 
                                            label="✓ Validé" 
                                            size="small" 
                                            color="success"
                                          />
                                        ) : (
                                          <Chip 
                                            label="⏳ En attente" 
                                            size="small" 
                                            color="warning"
                                          />
                                        )}
                                      </Box>
                                      <Divider sx={{ my: 1 }} />
                                      <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                          <Typography variant="body2" color="text.secondary">
                                            Inscrit le:
                                          </Typography>
                                          <Typography variant="body2">
                                            {new Date(referral.joinedAt).toLocaleDateString('fr-FR')}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                          <Typography variant="body2" color="text.secondary">
                                            Commandes:
                                          </Typography>
                                          <Typography variant="body2">
                                            {referral.ordersCount}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                          <Typography variant="body2" color="text.secondary">
                                            Total dépensé:
                                          </Typography>
                                          <Typography variant="body2" fontWeight="bold" color={referral.isValidated ? 'success.main' : 'primary'}>
                                            {new Intl.NumberFormat('fr-FR', {
                                              style: 'currency',
                                              currency: 'EUR',
                                            }).format(referral.totalSpent || 0)}
                                          </Typography>
                                          {!referral.isValidated && referral.remainingToValidate > 0 && (
                                            <Typography variant="caption" color="warning.main" display="block" mt={0.5}>
                                              Reste {new Intl.NumberFormat('fr-FR', {
                                                style: 'currency',
                                                currency: 'EUR',
                                              }).format(referral.remainingToValidate)} pour validation
                                            </Typography>
                                          )}
                                        </Grid>
                                      </Grid>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Paper>
                      </Grid>
                    )}

                    {referralData?.referrals && referralData.referrals.length === 0 && (
                      <Grid item xs={12}>
                        <Alert severity="info">
                          Vous n'avez pas encore de filleuls. Partagez votre code de parrainage pour commencer !
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                )}
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        borderTop: '1px solid',
        borderColor: 'divider',
      }}>
        <Button onClick={onClose} variant="contained">
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Page principale des utilisateurs
const Users = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [vouchersModalOpen, setVouchersModalOpen] = useState(false);
  const { showNotification } = useNotification();

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await usersService.getAllUsers(page, 10, searchTerm);
      setUsers(response.users);
      setTotalPages(response.meta.lastPage || 1);
    } catch (error) {
      showNotification('Erreur lors du chargement des utilisateurs', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, searchTerm]);

  // Détecter les paramètres de l'URL pour ouvrir automatiquement le modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userId = params.get('userId');
    const openModal = params.get('openModal');

    if (userId && openModal === 'true') {
      // Charger l'utilisateur par son ID
      const loadAndOpenUser = async () => {
        try {
          const response = await usersService.getUserById(parseInt(userId));
          if (response.user) {
            setSelectedUser(response.user);
            setDetailsModalOpen(true);
            // Nettoyer l'URL après ouverture
            navigate('/users', { replace: true });
          }
        } catch (error) {
          showNotification('Utilisateur introuvable', 'error');
          navigate('/users', { replace: true });
        }
      };
      loadAndOpenUser();
    }
  }, [location.search]);

  const handleSearch = () => {
    setPage(1);
    loadUsers();
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
    setSelectedUser(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
        Gestion des utilisateurs
      </Typography>

      {/* Barre de recherche */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Rechercher un utilisateur"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ flexGrow: 1 }}
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
        >
          Rechercher
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadUsers}
        >
          Actualiser
        </Button>
      </Box>

      {/* Table des utilisateurs */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Date d'inscription</TableCell>
              <TableCell>Panier</TableCell>
              <TableCell>Commandes</TableCell>
              <TableCell>Adresses</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Aucun utilisateur trouvé
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {user.first_name?.charAt(0) || 'U'}{user.last_name?.charAt(0) || 'N'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {(user.first_name || 'Non renseigné')} {(user.last_name || 'Non renseigné')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Niveau {user.stats?.profil?.level || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={user.stats?.cartItems || 0} 
                      size="small" 
                      color={user.stats?.cartItems > 0 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.stats?.orders || 0} 
                      size="small" 
                      color={user.stats?.orders > 0 ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.stats?.addresses || 0} 
                      size="small" 
                      color={user.stats?.addresses > 0 ? 'info' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleViewDetails(user)}
                      title="Voir les détails"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      color="secondary"
                      onClick={() => {
                        setSelectedUser(user);
                        setVouchersModalOpen(true);
                      }}
                      title="Bons de réduction"
                    >
                      <VoucherIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Modal de détails */}
      <UserDetailsModal
        open={detailsModalOpen}
        onClose={handleCloseDetails}
        user={selectedUser}
      />

      {/* Modal des bons de réduction */}
      <UserVouchersModal
        open={vouchersModalOpen}
        onClose={() => {
          setVouchersModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />
    </Box>
  );
};

export default Users;
